import { track } from '@/lib/telemetry';

export type SyncEffect = {
  id: string;
  type: string;
  payload: any;
  timestamp: string;
  /** Number of failed processing attempts. Absent on legacy entries (treated as 0). */
  attempts?: number;
  /** Last server/handler error message, set by recordEffectFailure. */
  lastError?: string;
};

/** A SyncEffect that exhausted its retry budget, parked in the dead-letter queue. */
export type FailedSyncEffect = SyncEffect & {
  attempts: number;
  lastError: string;
  /** ISO timestamp the effect was moved to the dead-letter queue. */
  failedAt: string;
};

const SYNC_QUEUE_KEY = 'team-v-does-sync-queue';
const DEAD_LETTER_KEY = 'team-v-does-sync-dead-letter';

/** After this many failed attempts, an effect moves to the dead-letter queue. */
export const SYNC_MAX_ATTEMPTS = 5;
/** Cap on dead-letter queue size — older entries are evicted FIFO. */
const DEAD_LETTER_MAX = 200;
/** Live-queue entries older than this are dropped by `pruneStaleEffects`. */
const MAX_EFFECT_AGE_MS = 24 * 60 * 60 * 1000; // 24h

export const getSyncQueue = (): SyncEffect[] => {
  try {
    const raw = localStorage.getItem(SYNC_QUEUE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch (e) {
    console.error('Failed to load sync queue', e);
    return [];
  }
};

export const saveSyncQueue = (queue: SyncEffect[]) => {
  try {
    localStorage.setItem(SYNC_QUEUE_KEY, JSON.stringify(queue));
  } catch (e) {
    console.error('Failed to save sync queue', e);
  }
};

export const enqueueEffect = (type: string, payload: any) => {
  const queue = getSyncQueue();
  const effect: SyncEffect = {
    id: `sync-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
    type,
    payload,
    timestamp: new Date().toISOString(),
    attempts: 0,
  };
  queue.push(effect);
  saveSyncQueue(queue);
  return effect;
};

export const removeEffect = (id: string) => {
  const queue = getSyncQueue();
  const filtered = queue.filter((e) => e.id !== id);
  saveSyncQueue(filtered);
};

export const clearSyncQueue = () => {
  saveSyncQueue([]);
};

// ─── Dead-letter (failed sync) queue ─────────────────────────────────────

export const getFailedEffects = (): FailedSyncEffect[] => {
  try {
    const raw = localStorage.getItem(DEAD_LETTER_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch (e) {
    console.error('Failed to load dead-letter queue', e);
    return [];
  }
};

const saveFailedEffects = (failed: FailedSyncEffect[]) => {
  try {
    // FIFO cap so a flapping handler can't blow out localStorage.
    const trimmed = failed.length > DEAD_LETTER_MAX ? failed.slice(-DEAD_LETTER_MAX) : failed;
    localStorage.setItem(DEAD_LETTER_KEY, JSON.stringify(trimmed));
  } catch (e) {
    console.error('Failed to save dead-letter queue', e);
  }
};

/**
 * Record a processing failure for an effect. Increments its `attempts` counter
 * and stores the last error message. When `attempts` reaches `SYNC_MAX_ATTEMPTS`,
 * the effect is removed from the live queue and moved to the dead-letter queue
 * so the user can review/retry/discard it instead of silently losing data.
 *
 * Returns true if the effect was moved to the dead-letter queue.
 */
export const recordEffectFailure = (id: string, error: unknown): boolean => {
  const queue = getSyncQueue();
  const idx = queue.findIndex((e) => e.id === id);
  if (idx === -1) return false;

  const effect = queue[idx];
  const attempts = (effect.attempts ?? 0) + 1;
  const lastError = errorToString(error);

  if (attempts >= SYNC_MAX_ATTEMPTS) {
    // Park it in the dead-letter queue and drop from the live queue.
    const failed = getFailedEffects();
    failed.push({ ...effect, attempts, lastError, failedAt: new Date().toISOString() });
    saveFailedEffects(failed);
    queue.splice(idx, 1);
    saveSyncQueue(queue);
    // Telemetry: a mutation just exhausted its retry budget. Cross-reference
    // with `offline_sync_auto_retried` to measure auto-retry success rate
    // (an effect parked, then auto-retried, that doesn't park again = drained).
    track('offline_sync_parked', {
      kind: effect.type,
      category: categorizeSyncError(lastError),
    });
    return true;
  }

  queue[idx] = { ...effect, attempts, lastError };
  saveSyncQueue(queue);
  return false;
};

/**
 * Retry a dead-lettered effect — moves it back to the live queue with a fresh
 * attempts counter so handlers will pick it up on the next processQueue tick.
 */
export const retryFailedEffect = (id: string) => {
  const failed = getFailedEffects();
  const target = failed.find((e) => e.id === id);
  if (!target) return;
  saveFailedEffects(failed.filter((e) => e.id !== id));

  const queue = getSyncQueue();
  // Strip dead-letter-only fields and reset attempts/lastError.
  const { failedAt: _failedAt, lastError: _lastError, ...rest } = target;
  queue.push({ ...rest, attempts: 0, timestamp: new Date().toISOString() });
  saveSyncQueue(queue);
};

export const discardFailedEffect = (id: string) => {
  const failed = getFailedEffects();
  saveFailedEffects(failed.filter((e) => e.id !== id));
};

export const clearFailedEffects = () => {
  saveFailedEffects([]);
};

/**
 * Coarse error category derived from the `lastError` string. Keeps the recovery
 * UI actionable ("5 failed — likely auth") without needing structured error
 * metadata at every handler call site. Heuristic-only; falls back to 'unknown'.
 *
 * - auth: session expired / invalid JWT / not authenticated → user re-auths and retries
 * - permission: RLS / 42501 / forbidden → admin action needed, retry won't help
 * - network: offline / fetch failed / timeout → automatic retry once back online
 * - validation: 400 / constraint / schema → payload is bad, retrying as-is won't help
 * - server: 5xx / internal / temporarily unavailable → retry later
 * - unknown: anything we can't pattern-match
 */
export type SyncErrorCategory = 'auth' | 'permission' | 'network' | 'validation' | 'server' | 'unknown';

export const categorizeSyncError = (lastError: string | undefined | null): SyncErrorCategory => {
  if (!lastError) return 'unknown';
  const e = lastError.toLowerCase();

  // Permission first — Postgres 42501 (insufficient_privilege) and RLS denials
  // can also include "JWT" wording, so this must beat the auth check.
  if (
    e.includes('42501') ||
    e.includes('insufficient_privilege') ||
    e.includes('permission denied') ||
    e.includes('not allowed') ||
    e.includes('forbidden') ||
    e.includes('row-level security') ||
    e.includes('rls')
  ) {
    return 'permission';
  }

  if (
    e.includes('jwt') ||
    e.includes('auth') && (e.includes('expired') || e.includes('invalid') || e.includes('missing')) ||
    e.includes('not authenticated') ||
    e.includes('unauthorized') ||
    e.includes('401') ||
    e.includes('session') && e.includes('expired')
  ) {
    return 'auth';
  }

  if (
    e.includes('failed to fetch') ||
    e.includes('network') ||
    e.includes('offline') ||
    e.includes('timeout') ||
    e.includes('timed out') ||
    e.includes('econnrefused') ||
    e.includes('enotfound') ||
    e.includes('socket hang up')
  ) {
    return 'network';
  }

  // Postgres validation codes: 23xxx (integrity constraint), 22xxx (data exception)
  if (
    /\b23\d{3}\b/.test(e) ||
    /\b22\d{3}\b/.test(e) ||
    e.includes('violates') ||
    e.includes('constraint') ||
    e.includes('invalid input') ||
    e.includes('400 ') ||
    e.startsWith('400') ||
    e.includes('bad request') ||
    e.includes('schema')
  ) {
    return 'validation';
  }

  if (
    /\b5\d{2}\b/.test(e) ||
    e.includes('internal server') ||
    e.includes('service unavailable') ||
    e.includes('bad gateway') ||
    e.includes('gateway timeout')
  ) {
    return 'server';
  }

  return 'unknown';
};

/** Aggregate category counts across a list of failed effects. */
export const summarizeFailedCategories = (
  failed: Pick<FailedSyncEffect, 'lastError'>[],
): Record<SyncErrorCategory, number> => {
  const counts: Record<SyncErrorCategory, number> = {
    auth: 0,
    permission: 0,
    network: 0,
    validation: 0,
    server: 0,
    unknown: 0,
  };
  for (const e of failed) counts[categorizeSyncError(e.lastError)]++;
  return counts;
};

/**
 * Bulk-retry every parked effect whose `lastError` matches the given category.
 * Returns the number of effects moved back to the live queue. Lets callers
 * (e.g. the auto-retry-on-online surface) drain a single failure mode without
 * iterating individual ids in the UI layer.
 */
export const retryFailedEffectsByCategory = (category: SyncErrorCategory): number => {
  const failed = getFailedEffects();
  const matches = failed.filter((e) => categorizeSyncError(e.lastError) === category);
  for (const e of matches) retryFailedEffect(e.id);
  return matches.length;
};

/** The single category that accounts for the most parked effects, or null when empty. */
export const dominantFailedCategory = (
  failed: Pick<FailedSyncEffect, 'lastError'>[],
): SyncErrorCategory | null => {
  if (failed.length === 0) return null;
  const counts = summarizeFailedCategories(failed);
  let best: SyncErrorCategory = 'unknown';
  let bestN = -1;
  for (const k of Object.keys(counts) as SyncErrorCategory[]) {
    if (counts[k] > bestN) {
      best = k;
      bestN = counts[k];
    }
  }
  return bestN > 0 ? best : null;
};

function errorToString(error: unknown): string {
  if (!error) return 'Unknown error';
  if (typeof error === 'string') return error;
  if (error instanceof Error) return error.message || error.name;
  if (typeof error === 'object') {
    // Supabase error shape: { message, code, details }
    const e = error as Record<string, unknown>;
    const parts = [e.code, e.message, e.details].filter(Boolean);
    if (parts.length) return parts.join(' — ');
    try {
      return JSON.stringify(error);
    } catch {
      return String(error);
    }
  }
  return String(error);
}

/**
 * Prune stuck or very aged entries from the live queue (recovery from bugs).
 * Does not modify the dead-letter queue.
 */
export const pruneStaleEffects = (): number => {
  const queue = getSyncQueue();
  const now = Date.now();
  const kept = queue.filter((e) => {
    const age = now - new Date(e.timestamp).getTime();
    return age <= MAX_EFFECT_AGE_MS && (e.attempts ?? 0) < SYNC_MAX_ATTEMPTS;
  });
  if (kept.length !== queue.length) {
    saveSyncQueue(kept);
    console.warn(`syncQueue: pruned ${queue.length - kept.length} stale effects`);
  }
  return queue.length - kept.length;
};
